/* Header behavior — loaded as a deferred module from sections/header.liquid.
   State lives in data attributes; all visuals are in assets/section-header.css:
   - <sticky-header data-drawer-open> / html[data-drawer-locked] — mobile drawer + scroll lock
   - <header-nav data-path="…">                                  — which L1 mega is open (mobile)
   - .mega[data-sub-path="…"]                                    — which L2's L3 panel is open (mobile) */

// Desktop mega L2→L3 hover-intent: ms to wait before switching to a newly-hovered
// L2 so a diagonal move toward the open L3 (crossing other L2 rows) doesn't swap
// it. First open (nothing active yet) is instant; only switches are delayed.
// Bump toward 500 for a longer grace period.
const MEGA_SWITCH_DELAY = 300;
// How long the pointer must linger on neutral space (cards / empty area — not an
// L2 row and not the open L3) before the open L3 flyout auto-closes.
const MEGA_CLOSE_DELAY = 500;
// Desktop L1 hover-intent: ms the pointer must rest on an L1 before the first
// mega opens, so passing over the strip on the way to the search bar doesn't
// pop a panel. Once a mega is open, moving to another L1 switches instantly.
// See #l1Hover.
const L1_OPEN_DELAY = 200;
// Desktop mega on a short viewport: px of breathing room left between the
// bottom of an open panel and the bottom of the window. See #sizeMegas.
const MEGA_VIEWPORT_GAP = 12;
// Never cap a panel shorter than this — on an absurdly short window a sliver of
// a menu is worse than one that runs a little past the fold.
const MEGA_MIN_HEIGHT = 200;

class StickyHeader extends HTMLElement {
  #lockedScrollY = 0;
  #megaSwitchTimer = null;
  #megaLastHovered = null;
  #l1SwitchTimer = null;
  #l1LastHovered = null;
  #megaSizeFrame = null;

  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-action="toggle-drawer"]');
    this.nav = this.querySelector('header-nav');
    // Keep in sync with the CSS breakpoint in section-header.css — below
    // 1366px the desktop menu doesn't fit and the drawer header is used.
    this.mobileQuery = window.matchMedia('(max-width: 1365px)');
    this.toggleBtn?.addEventListener('click', () => this.toggleDrawer());
    this.addEventListener('click', (e) => this.handleClick(e));
    // The drawer (and its scroll lock) is mobile-only — crossing into the
    // desktop breakpoint while open would leave the page locked forever.
    this.mobileQuery.addEventListener('change', (e) => {
      if (!e.matches) this.closeDrawer();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dataset.drawerOpen !== undefined) this.closeDrawer();
    });

    // Desktop mega: hover-intent delay on L2→L3 switching (see #megaHover +
    // section-header.css). mouseover is delegated on the header; a per-mega
    // mouseleave resets the open L3 when the pointer leaves that mega.
    this.addEventListener('mouseover', (e) => { this.#l1Hover(e); this.#megaHover(e); });
    for (const mega of this.querySelectorAll('.mega')) {
      mega.addEventListener('mouseleave', () => this.#resetMegaActive(mega));
    }
    // Desktop L1: close the open mega when the pointer leaves the whole nav.
    // The dropped panel is a DOM descendant of .nav--l1 (positioned below it),
    // so moving from the bar into the mega does NOT trigger this.
    this.querySelector('.nav--l1')?.addEventListener('mouseleave', () => this.#closeL1());

    // Desktop mega sizing (see #sizeMegas). focusin covers the keyboard path,
    // which opens a panel purely through CSS :has(:focus-visible) with no JS
    // involved — measuring here still lands before the browser paints it.
    // resize and scroll re-measure while a panel is open, since the window can
    // change size and the promo bar above the sticky header scrolls away,
    // which frees up room the panel should be allowed to use.
    this.nav?.addEventListener('focusin', () => this.#sizeMegas());
    window.addEventListener('resize', () => this.#scheduleMegaSize());
    window.addEventListener('scroll', () => {
      if (this.#activeL1() || this.nav?.contains(document.activeElement)) this.#scheduleMegaSize();
    }, { passive: true });
    this.#sizeMegas();

    // The search panel and the drawer must never be open at once. Algolia's
    // mobile panel is a detached overlay appended to <body>, so it covers the
    // page while the drawer stays on top of it — two open menus fighting for
    // the same screen. Close the drawer as soon as the search field is engaged.
    //
    // Bound to the header's own field, not Algolia's overlay: that overlay is
    // created and destroyed on every open, so there is no stable node to watch.
    // pointerdown fires before Algolia opens the panel (no flash of both), and
    // focusin covers reaching the field by keyboard. closeDrawer() is guarded,
    // so both firing for one tap is harmless. Above 1366px there is no drawer
    // and dataset.drawerOpen is never set, so this is inert on desktop.
    const search = this.querySelector('.header__search');
    const closeForSearch = () => {
      if (this.dataset.drawerOpen !== undefined) this.closeDrawer();
    };
    search?.addEventListener('pointerdown', closeForSearch);
    search?.addEventListener('focusin', closeForSearch);
  }

  toggleDrawer() {
    const open = this.dataset.drawerOpen !== undefined;
    open ? this.closeDrawer() : this.openDrawer();
  }

  openDrawer() {
    this.dataset.drawerOpen = '';
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    // Scroll lock: record the offset, then freeze <body> in place via CSS
    // (html[data-drawer-locked] body { position: fixed; ... } in
    // section-header.css). position:fixed is the only lock iOS Safari
    // can't rubber-band through, and it sidesteps the overflow:hidden
    // bug that breaks the sticky header.
    this.#lockedScrollY = window.scrollY;
    document.documentElement.style.setProperty('--drawer-scroll-y', `${this.#lockedScrollY}px`);
    document.documentElement.dataset.drawerLocked = '';
  }

  closeDrawer() {
    const wasLocked = document.documentElement.dataset.drawerLocked !== undefined;
    delete this.dataset.drawerOpen;
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
    delete document.documentElement.dataset.drawerLocked;
    document.documentElement.style.removeProperty('--drawer-scroll-y');
    if (this.nav) this.nav.dataset.path = '';
    for (const mega of this.querySelectorAll('.mega[data-sub-path]')) this.#clearSubPath(mega);
    // Unfreezing <body> collapses the page scroll to 0 — restore it in the
    // same frame (before paint) so nothing visibly jumps.
    if (wasLocked) window.scrollTo({ top: this.#lockedScrollY, behavior: 'instant' });
  }

  handleClick(e) {
    const isMobile = this.mobileQuery.matches;

    // Tablet scrim: clicking the dimmed page beside the 480px drawer closes it.
    if (e.target.closest('[data-action="close-drawer"]')) {
      this.closeDrawer();
      return;
    }

    // Account footer row: <shopify-account> only makes its own avatar
    // clickable, so a click on the label (or the row's padding) does
    // nothing. Forward those clicks into the component. Clicks already
    // inside <shopify-account> fall through untouched — this also guards
    // against the forwarded click re-entering this handler and recursing
    // (shadow-internal clicks retarget to the host).
    const account = e.target.closest('.nav__footer-item--account');
    if (account && !e.target.closest('shopify-account')) {
      this.#openAccount(account.querySelector('shopify-account'));
      return;
    }

    // L1 → L2: the chevron button, or (mobile) anywhere on the row.
    const enterBtn = e.target.closest('[data-action="enter-mega"]');
    const l1Row = isMobile ? e.target.closest('.nav__item[data-has-mega] > .nav__row') : null;
    if (enterBtn || l1Row) {
      e.preventDefault();
      this.enterMega((enterBtn || l1Row).closest('.nav__item'));
      return;
    }

    // Back one level: anywhere on the mega header (container carries the action).
    const exit = e.target.closest('[data-action="exit-mega"]');
    if (exit) {
      e.preventDefault();
      this.exitMega(exit);
      return;
    }

    // L2 → L3 (mobile): the whole item drills into the third level.
    const l2Link = isMobile ? e.target.closest('.mega__l2-item--has-l3 > .mega__l2-link') : null;
    if (l2Link) {
      e.preventDefault();
      this.enterSubPath(l2Link.parentElement);
    }
  }

  enterMega(item) {
    if (!this.nav || !item) return;
    this.nav.dataset.path = item.dataset.handle || '';
    for (const node of this.nav.querySelectorAll('.nav--l1 > .nav__item')) delete node.dataset.onPath;
    item.dataset.onPath = '';
    item.querySelector('[data-action="enter-mega"]')?.setAttribute('aria-expanded', 'true');
    this.nav.scrollTop = 0;
  }

  enterSubPath(item) {
    const mega = item?.closest('.mega');
    if (!mega) return;
    mega.dataset.subPath = item.dataset.handle || '';
    for (const node of mega.querySelectorAll('.mega__l2-item')) delete node.dataset.onSubPath;
    item.dataset.onSubPath = '';
    // Surface the L2 title in the mega-header breadcrumb ("GIRL / CLOTHING").
    const crumb = mega.querySelector('.mega__title--sub');
    if (crumb) crumb.textContent = item.querySelector('.mega__l2-link span')?.textContent.trim() || '';
    if (this.nav) this.nav.scrollTop = 0;
  }

  exitMega(el) {
    if (!this.nav) return;
    const mega = el.closest('.mega');
    // From L3, step back to L2 first; from L2, back to the root list.
    if (mega?.dataset.subPath !== undefined) {
      this.#clearSubPath(mega);
    } else {
      this.nav.dataset.path = '';
      for (const node of this.nav.querySelectorAll('.nav--l1 > .nav__item')) delete node.dataset.onPath;
      for (const node of this.querySelectorAll('[data-action="enter-mega"]')) node.setAttribute('aria-expanded', 'false');
    }
    this.nav.scrollTop = 0;
  }

  #clearSubPath(mega) {
    delete mega.dataset.subPath;
    for (const node of mega.querySelectorAll('.mega__l2-item')) delete node.dataset.onSubPath;
    const crumb = mega.querySelector('.mega__title--sub');
    if (crumb) crumb.textContent = '';
  }

  #openAccount(el) {
    if (!el) return;
    // The component's click handler lives on internals behind its (open)
    // shadow root — a .click() on the host fires an event nobody handles.
    // Click what actually opens the sheet: the slotted avatar (signed-out
    // state, rendered inside the internal button), else the shadow-root
    // button (signed-in state), else fall back to the host.
    const target =
      el.querySelector('[slot="signed-out-avatar"]') ||
      el.shadowRoot?.querySelector('button, [role="button"], a') ||
      el;
    target.click();
  }

  // ---- Desktop mega L2 → L3 hover-intent ----------------------------------
  // Switching to a newly-hovered L2 waits MEGA_SWITCH_DELAY so dragging the
  // pointer diagonally to the open L3 (crossing other L2 rows) doesn't switch
  // it. First open (nothing active yet) is instant. L3 visibility is driven by
  // [data-active] in section-header.css.
  #megaHover(e) {
    if (this.mobileQuery.matches) return; // desktop only
    const mega = e.target.closest('.mega');
    if (!mega) return;
    const item = e.target.closest('.mega__l2-item');
    // Neutral space — cards column / mega header / empty grid area (not an L2
    // row, and not the open L3, which resolves to its own L2 item). Linger here
    // and the open L3 auto-closes after MEGA_CLOSE_DELAY; moving back onto an L2
    // or the L3 before then cancels it.
    if (!item) {
      if (this.#megaLastHovered === 'neutral') return; // already scheduled
      this.#megaLastHovered = 'neutral';
      this.#clearMegaTimer();
      if (mega.querySelector('.mega__l2-item[data-active]')) {
        this.#megaSwitchTimer = setTimeout(() => this.#activateL2(mega, null), MEGA_CLOSE_DELAY);
      }
      return;
    }
    if (item === this.#megaLastHovered) return; // ignore repeat events over the same row
    this.#megaLastHovered = item;
    // Already the open one — its L3 fills column 2, so hovering the L3 lands here too.
    if (item.dataset.active !== undefined) {
      this.#clearMegaTimer();
      return;
    }

    const hasL3 = item.classList.contains('mega__l2-item--has-l3');
    const target = hasL3 ? item : null; // a no-L3 row (e.g. "View all") closes the open L3
    const anyOpen = mega.querySelector('.mega__l2-item[data-active]');

    if (!anyOpen && hasL3) {
      this.#activateL2(mega, target); // nothing open yet → open immediately
    } else {
      this.#clearMegaTimer();
      this.#megaSwitchTimer = setTimeout(() => this.#activateL2(mega, target), MEGA_SWITCH_DELAY);
    }
  }

  #activateL2(mega, item) {
    this.#clearMegaTimer();
    for (const node of mega.querySelectorAll('.mega__l2-item[data-active]')) {
      if (node !== item) delete node.dataset.active;
    }
    if (item) item.dataset.active = '';
  }

  #clearMegaTimer() {
    if (this.#megaSwitchTimer) {
      clearTimeout(this.#megaSwitchTimer);
      this.#megaSwitchTimer = null;
    }
  }

  #resetMegaActive(mega) {
    if (!mega) return;
    this.#clearMegaTimer();
    this.#megaLastHovered = null;
    for (const node of mega.querySelectorAll('.mega__l2-item[data-active]')) delete node.dataset.active;
  }

  // ---- Desktop L1 → mega hover-intent -------------------------------------
  // Passing over the top-level strip (e.g. on the way up to the search bar)
  // shouldn't pop a mega. The first open waits L1_OPEN_DELAY, retargeted if the
  // pointer moves to another L1 and cancelled if it leaves the nav first. Once a
  // mega is open, switching to a different L1 is instant. Visibility is driven
  // by [data-active] in section-header.css.
  #l1Hover(e) {
    if (this.mobileQuery.matches || !this.nav) return; // desktop only
    const l1 = e.target.closest('.nav--l1 > .nav__item');
    if (!l1) return; // neutral gap; leaving the nav entirely is handled by #closeL1
    const active = this.#activeL1();
    if (l1 === active) { this.#clearL1Timer(); this.#l1LastHovered = l1; return; }
    if (l1 === this.#l1LastHovered) return; // ignore repeat events over the same row
    this.#l1LastHovered = l1;
    const target = l1.hasAttribute('data-has-mega') ? l1 : null; // plain link → close open mega
    if (active) {
      this.#activateL1(target); // a mega is already open → switch immediately
    } else {
      this.#clearL1Timer();
      if (target) this.#l1SwitchTimer = setTimeout(() => this.#activateL1(target), L1_OPEN_DELAY);
    }
  }

  #activeL1() {
    return this.nav?.querySelector('.nav--l1 > .nav__item[data-active]') || null;
  }

  #activateL1(item) {
    this.#clearL1Timer();
    for (const node of this.nav.querySelectorAll('.nav--l1 > .nav__item[data-active]')) {
      if (node !== item) {
        delete node.dataset.active;
        this.#resetMegaActive(node.querySelector('.mega')); // reset its L2/L3 for the next open
      }
    }
    if (item) {
      this.#sizeMegas(); // before the reveal, so the cap is right on first paint
      item.dataset.active = '';
    }
  }

  // ---- Desktop mega: cap the panel to the room left below it ---------------
  // The panel hangs off a position:sticky header, so on a short viewport
  // anything past the fold is unreachable: scrolling moves the page while the
  // header — and the panel with it — stays pinned. Measure the room actually
  // left below the panel's top edge and hand it to CSS as --mega-max-h, which
  // turns the overflow into an internal scroll (section-header.css, section 5).
  //
  // Measured off .nav--l1, not the panel: .nav--l1 is the panel's containing
  // block (the panel sits at top:100% of it) and is always rendered, whereas
  // the panel itself is display:none until it opens, so its own rect reads 0.
  //
  // The var lands on <header-nav> so one write covers every panel.
  #sizeMegas() {
    if (this.mobileQuery.matches || !this.nav) return;
    const l1 = this.nav.querySelector('.nav--l1');
    if (!l1) return;
    const room = window.innerHeight - l1.getBoundingClientRect().bottom - MEGA_VIEWPORT_GAP;
    this.nav.style.setProperty('--mega-max-h', `${Math.round(Math.max(room, MEGA_MIN_HEIGHT))}px`);
  }

  #scheduleMegaSize() {
    if (this.#megaSizeFrame) return;
    this.#megaSizeFrame = requestAnimationFrame(() => {
      this.#megaSizeFrame = null;
      this.#sizeMegas();
    });
  }

  #clearL1Timer() {
    if (this.#l1SwitchTimer) {
      clearTimeout(this.#l1SwitchTimer);
      this.#l1SwitchTimer = null;
    }
  }

  #closeL1() {
    this.#clearL1Timer();
    this.#l1LastHovered = null;
    this.#activateL1(null);
  }
}

if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

class HeaderNav extends HTMLElement {}
if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);

/* Localization <select> auto-width is now CSS-only (no JS, no load jump): a ghost sizer
   <span> carrying the select's class sizes the wrapper by the SELECTED option's text, and
   the select is absolutely positioned to fill it. See snippets/localization-country-form.liquid
   + .localization-select-wrap in css--critical-header / section-header.css. */
